/**
 * Reactive error detection for truly unsupported browsers.
 *
 * Instead of proactively blocking browsers via UA sniffing (which causes
 * false positives in in-app webviews and blocks paying customers), we let
 * polyfills handle API gaps and only catch the rare crash from a genuinely
 * ancient browser (pre-2017) that is missing non-polyfillable primitives
 * like Proxy, Reflect, or dynamic import().
 *
 * Used by ErrorBoundary to redirect to /unsupported-browser on catch.
 */
class BrowserCompatibilityHeuristics {
  /**
   * Determines if an error is likely caused by an outdated browser
   * missing non-polyfillable language primitives or APIs that should
   * have been polyfilled but weren't.
   */
  static isErrorDueToOutdatedBrowser(error: Error): boolean {
    const outdatedBrowserErrors = [
      /Proxy is not defined/,
      /Reflect is not defined/,
      /import\b.*not supported/i,
      /WeakMap is not defined/,
      /Promise is not defined/,
      /Map is not defined/,
      /Set is not defined/,
      /BroadcastChannel is not defined/,
      /structuredClone is not defined/,
      /replaceAll is not a function/,
      /hasOwn is not a function/,
      /ResizeObserver is not defined/,
    ];

    return outdatedBrowserErrors.some((pattern) => pattern.test(error.message));
  }
}

export default BrowserCompatibilityHeuristics;
